import React, { useState, useEffect, useCallback } from 'react';
import App from './App'; // Existing order wizard app
import i18n from './i18n';
import AuthPage from './components/AuthPage';
import VerifyEmail from './components/VerifyEmail';
import ResetPassword from './components/ResetPassword';
import LegalPage from './components/LegalPage';
import AdminPanel from './components/AdminPanel';
import UserPanel from './components/UserPanel';
import DesignPreview from './components/DesignPreview';
import LanguageOnboardingBanner from './components/LanguageOnboardingBanner';
import { AuthProvider } from './contexts/AuthContext';
import { install401Interceptor } from './utils/authInterceptor';
import { installPollingGuard } from './utils/pollingGuard';
import { useSmartPoll } from './hooks/useSmartPoll';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// One-shot: any 401 from /api/* (login excluded) flips an `auth:expired`
// event that the router below converts into a logout. Lives outside the
// component so React StrictMode's double-mount doesn't install twice.
install401Interceptor();
// One-shot: hidden tabs stop polling + failing GETs back off. Must be
// installed AFTER the 401 interceptor so both fetch wrappers compose.
installPollingGuard();

export default function AppRouter() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const savedToken = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const savedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (savedToken && savedUser) {
      setToken(savedToken);
      const parsed = JSON.parse(savedUser);
      setUser(parsed);
      // Restore saved language preference on app boot. Same admin-gate
      // as in handleLoginSuccess.
      if (parsed?.role !== 'admin' && parsed?.language) {
        try { i18n.changeLanguage(parsed.language); } catch (_) {}
      }
    }
    setIsLoading(false);
  }, []);

  // Refresh the user object from /api/auth/me — keeps balance / reserved in sync.
  // `fetchMe` throws on failure so useSmartPoll can back off; `refreshUser`
  // is the silent variant handed to child components.
  const fetchMe = useCallback(async () => {
    if (!token) return;
    const res = await fetch(`${API_URL}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error(`auth/me ${res.status}`);
    const fresh = await res.json();
    setUser(fresh);
    const store = localStorage.getItem('auth_token') ? localStorage : sessionStorage;
    store.setItem('user', JSON.stringify(fresh));
  }, [token]);

  const refreshUser = useCallback(() => fetchMe().catch(() => {}), [fetchMe]);

  // Keep the cached user (balance, reserved) fresh WITHOUT hammering the
  // API: once on boot, once whenever the tab regains focus, and at most
  // every 5 minutes while the tab is actually visible. Anything that
  // changes the balance (new order, recharge) calls `refreshUser`
  // directly, so this is only a safety net.
  // NOTE: the deps below use `user?.role`, never the `user` object —
  // depending on the object made `refreshUser` → setUser → effect
  // re-run → `refreshUser` an endless request loop in production.
  const isCustomer = !!token && !!user && user.role !== 'admin';
  useSmartPoll(fetchMe, 300000, { enabled: isCustomer });

  const handleLoginSuccess = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    // Apply the user's stored language preference (if any) immediately
    // after login so the UI flips before the dashboard renders. Admin
    // is always EN by product decision and we deliberately don't read
    // user.language for admins.
    if (userData.role !== 'admin' && userData.language) {
      try { i18n.changeLanguage(userData.language); } catch (_) {}
    }
    if (userData.role === 'admin') {
      // Preserve deep-link into an admin section if the user was
      // already on `/admin/<section>` (e.g. clicked a bookmark that
      // required auth). Otherwise land on the default `/admin`.
      const cur = window.location.pathname || '';
      if (!cur.startsWith('/admin')) {
        window.location.href = '/admin';
      }
    }
  };

  const handleLogout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    sessionStorage.removeItem('auth_token');
    sessionStorage.removeItem('user');
    window.location.href = '/';
  }, []);

  // Global 401 → automatic logout. The interceptor (installed once at
  // module load) dispatches `auth:expired` on `window` whenever any
  // /api/* call (except login/register/MFA) comes back with 401.
  // Here we simply react to that event so the cleanup happens in ONE
  // place (handleLogout). A toast tells the user what just happened.
  useEffect(() => {
    const onExpired = () => {
      // Guard against firing during the initial bootstrap when no
      // session was present in storage anyway.
      if (!localStorage.getItem('auth_token') && !sessionStorage.getItem('auth_token')) return;
      try {
        toast.error('Your session has expired — please log in again.', { duration: 4000 });
      } catch (_) { /* sonner may not be mounted yet on cold load */ }
      // Tiny delay so the toast is visible before the hard navigation
      // wipes the page. 1 s is enough to read it without feeling
      // sluggish.
      setTimeout(() => handleLogout(), 1000);
    };
    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, [handleLogout]);

  const path = window.location.pathname;
  const search = window.location.search;

  // Design-system preview gate — `?preview=design` short-circuits the
  // router so the showcase page is reachable EVEN WHEN logged in as
  // admin (no auth shell, no AdminPanel chrome around it). Lives
  // before the loading spinner so it renders instantly.
  if (search.includes('preview=design')) {
    return <DesignPreview />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }
  if (path === '/verify-email') return <VerifyEmail />;
  if (path === '/reset-password') return <ResetPassword />;
  if (path === '/terms')           return <LegalPage slug="terms" />;
  if (path === '/privacy')         return <LegalPage slug="privacy" />;
  if (path === '/license')         return <LegalPage slug="license" />;
  if (path === '/refund-policy')   return <LegalPage slug="refund" />;
  if (!user || !token) return (
    <>
      <AuthPage onLoginSuccess={handleLoginSuccess} />
      {/* Onboarding banner — surface language picker on first visit.
          Mounted at the router level (not inside AuthPage) so it also
          shows up on reset-password / verify-email landings. Admin
          panel is intentionally excluded below. */}
      <LanguageOnboardingBanner />
    </>
  );
  if (user.role === 'admin') {
    return (
      <AuthProvider token={token}>
        <AdminPanel user={user} token={token} onLogout={handleLogout} refreshUser={refreshUser} />
      </AuthProvider>
    );
  }
  return (
    <AuthProvider token={token}>
      <UserPanel user={user} token={token} onLogout={handleLogout} refreshUser={refreshUser} />
      {/* Customer area also gets the onboarding banner for users who
          signed up but never explicitly picked a language. Dismisses
          once per browser via localStorage key. */}
      <LanguageOnboardingBanner />
    </AuthProvider>
  );
}
